import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import mixpanel from 'utils/mixpanel/mixpanel';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';
import * as Sentry from '@sentry/react';
import equal from 'deep-equal';

import { dig } from 'spiceutils';
import FFVis from 'ff-vis-js/dist/FFVis';

import { GRAPHQL_URL } from 'containers/App/constants';

import {
  newChart,
  setPptxCdf,
  setMetaData,
  showCsvDownload,
  chartLoading,
  chartLoadingComplete,
  dispatchError,
} from 'containers/App/actions';

import {
  makeSelectChartLoading,
  makeSelectControlList,
  makeSelectChartError,
  makeSelectMetaData,
  makeSelectPptxCdf,
} from 'containers/App/selectors';

import DownloadContainer from './DownloadButtons/downloadContainer';
import PptxButton from './DownloadButtons/pptx_button';
import CsvButton from './DownloadButtons/csv_button';

import ChartHeader from './ChartHeader';
import PinToBoardButton from './PinToBoardButton';

import StyledAnimatedLoader from './StyledAnimatedLoader';
import ErrorOverlay from './Errors/ErrorOverlay';

const createRef = () => {
  // eslint-disable-next-line func-names
  const ref = function (current) {
    ref.current = current;
  };

  return ref;
};

export class Chart extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      chart: null,
    };

    this.componentRefs = [
      'title',
      'subtitle',
      'chart',
      'source',
      'notes',
    ].reduce((refs, key) => ({
      ...refs,
      [key]: createRef(),
    }), {});

    this.getChartInfo = this.getChartInfo.bind(this);
  }
  componentDidMount() {
    const transaction = Sentry.getCurrentHub().getScope().getTransaction();
    if (transaction) {
      const span = transaction.startChild({ op: 'function', description: 'buildChartOnLoad' });
      this.buildChart(() => {
        span.finish();
      });
    } else {
      this.buildChart();
    }
  }
  componentDidUpdate(oldProps) {
    if (!equal(this.props.cdf, oldProps.cdf)) {
      const transaction = Sentry.getCurrentHub().getScope().getTransaction();
      if (transaction) {
        const span = transaction.startChild({ op: 'function', description: 'buildChartOnUpdate' });
        this.buildChart(() => {
          span.finish();
        });
      } else {
        this.buildChart();
      }
    }
  }
  getCDF() {
    return { CDF: this.props.cdf };
  }
  setCDF(cdf) {
    this.props.setNewChart(cdf);
  }
  getMetaData() {
    return {
      Metadata: this.props.metaData,
    };
  }
  setMetaData(metaData) {
    this.props.setMetaData(metaData);
  }
  setPptxCdf(pptxCdf) {
    const { setPptxCdf } = this.props;
    setPptxCdf(pptxCdf);
  }
  getChartInfo() {
    return this.state.chart.getBoardInformationForChart();
  }
  handleChartEvent(event, callback) {
    switch (event.type) {
      case 'PROCESSING_STARTED':
        this.props.loading(event);
        break;
      case 'PROCESSING_COMPLETE':
        this.props.loadingComplete(event);
        if (callback) {
          callback();
        }
        break;
      case 'ERROR':
        this.props.dispatchError(event);
        Sentry.captureException(new Error(event.message));
        break;
      default:
    }
  }
  buildChart(callback) {
    if (this.props.cdf.QuestionCode && this.props.cdf.Visualisation) {
      const chart = this.state.chart ||
        new FFVis.Chart({
          getCDF: () => this.getCDF(),
          setCDF: (cdf) => { this.setCDF(cdf); },
          processStatus: (event) => { this.handleChartEvent(event, callback); },
          getMetaData: () => this.getMetaData(),
          setMetaData: (metaData) => this.setMetaData(metaData),
          setNewCDF: (pptxCdf) => this.setPptxCdf(pptxCdf),
          graphQlUrl: GRAPHQL_URL,
          chartElements: Object.entries(this.componentRefs).reduce((elements, [key, ref]) => ({
            ...elements,
            [`${key}Div`]: ref.current,
          }), {}),
        });
      mixpanel.track('Use the Datatool', {
        question_code: this.getCDF().CDF.QuestionCode,
        cdf: this.getCDF(),
      });

      chart.buildChart();
      if (this.state.chart == null) {
        this.setState({ chart });
      }
    }
  }
  downloadCsv() {
    const chart = this.state.chart;
    const csvData = `data:text/csv;charset=utf-8, ${chart.getCSV()}`;
    this.props.showCsvDownload(encodeURI(csvData), this.props.cdf.QuestionCode);

    const {
      Title: title,
      Subtitle: subtitle,
    } = this.getChartInfo();

    mixpanel.track('Download Chart', {
      question_code: this.props.cdf.QuestionCode,
      chart_title: title,
      chart_subtitle: subtitle,
      download_type: 'CSV',
    });
  }

  render() {
    const {
      controlList,
      cdf,
      pptxCdf,

      showLoader,
      chartError,
    } = this.props;

    const showButtons = !chartError && !showLoader;

    const showPptxDownload = dig(controlList, ['Visualisation', 'pptxAvailable']);
    const showCsvDownload = dig(controlList, ['Visualisation', 'csvAvailable']);
    const showDownloadBox = showButtons && (showPptxDownload || showCsvDownload);
    return (
      <div>
        <ChartHeader
          pinToBoardButton={showButtons && (
            <PinToBoardButton getChartInfo={this.getChartInfo} />
          )}
          downloadButtons={showDownloadBox && (
            <DownloadContainer>
              {showPptxDownload && <PptxButton getChartInfo={this.getChartInfo} controlList={controlList} cdf={cdf} pptxCdf={pptxCdf} />}
              {showCsvDownload && <CsvButton controlList={controlList} onClick={() => { this.downloadCsv(); }} />}
            </DownloadContainer>
          )}
        />
        <ChartDiv>
          { showLoader && <StyledAnimatedLoader /> }
          { chartError &&
            <ErrorOverlay error={chartError} />
          }
          <div className="chart-container">
            <div className="heading">
              <div>
                <h2 id="chart_title" ref={this.componentRefs.title} />
                <p id="chart_subtitle" ref={this.componentRefs.subtitle} />
              </div>
            </div>
            <div id="OuterDiv">
              <div id="chart" ref={this.componentRefs.chart} />
            </div>
            <div id="chart_source" ref={this.componentRefs.source} />
            <div id="chart_notes" ref={this.componentRefs.notes} />
          </div>
        </ChartDiv>
      </div>
    );
  }
}


const mapStateToProps = createStructuredSelector({
  showLoader: makeSelectChartLoading(),
  chartError: makeSelectChartError(),
  controlList: makeSelectControlList(),
  metaData: makeSelectMetaData(),
  pptxCdf: makeSelectPptxCdf(),
});

export const mapDispatchToProps = (dispatch) => ({
  showCsvDownload: (url, code) => {
    dispatch(showCsvDownload(url, code));
  },
  setNewChart: (cdf) => {
    dispatch(newChart(cdf));
  },
  setMetaData: (metaData) => {
    dispatch(setMetaData(metaData));
  },
  setPptxCdf: (pptxCdf) => {
    dispatch(setPptxCdf(pptxCdf));
  },
  loading: () => {
    dispatch(chartLoading());
  },
  loadingComplete: () => {
    dispatch(chartLoadingComplete());
  },
  dispatchError: (event) => {
    dispatch(dispatchError(event.message));
  },
});


Chart.propTypes = {
  cdf: PropTypes.object,
  pptxCdf: PropTypes.object,
  showCsvDownload: PropTypes.func,
  setNewChart: PropTypes.func,
  loading: PropTypes.func,
  loadingComplete: PropTypes.func,
  dispatchError: PropTypes.func,
  showLoader: PropTypes.bool,
  chartError: PropTypes.object,
  controlList: PropTypes.object,
  metaData: PropTypes.object,
  setMetaData: PropTypes.func,
  setPptxCdf: PropTypes.func,
};

export const ChartDiv = styled.div`
  width: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  .chart-container{
    width: 95%;
    margin: 0 auto;
  }
  .heading{
    padding-left: 0;
    padding-right: 0;
    padding: 50px 0;
    *{
      margin: 0;
    }
    h2{
      margin-bottom: 20px;
      font-weight: bold;
    }
  }
  #OuterDiv{
    background-color: #ffffff;
    padding: 50px 0 20px 0;
    border: 1px solid #e4e4e4;
    border-bottom: none;
    #chart{
      width: 100%;
      margin: 0 auto;
      svg{
        width: 100%;
      }
    }
  }
  #chart_source{
    border: 1px solid #e4e4e4;
    border-top: none;
    font-size: 0.8em;
    background-color: #ffffff;
    padding: 20px 50px 40px 50px;
  }
  #chart_notes{
    margin-top: 10px;
    text-align: left;
    font-size: 0.8em;
    font-style: italic;
    margin-bottom: 20px;
    hr{
      width: 100%;
      margin: 20px 0;
    }
  }
`;

const withConnect = connect(mapStateToProps, mapDispatchToProps);

export default compose(
  withConnect,
)(Chart);



// WEBPACK FOOTER //
// ./app/components/Chart/index.js